import { useState, useCallback, useRef } from 'react';

interface RateLimitConfig {
  maxActions: number;
  windowMs: number;
}

interface RateLimitState {
  isLimited: boolean;
  remainingActions: number;
  resetTime: number | null;
}

export const useRateLimit = (config: RateLimitConfig) => {
  const [state, setState] = useState<RateLimitState>({
    isLimited: false,
    remainingActions: config.maxActions,
    resetTime: null,
  });
  
  const actionsRef = useRef<number[]>([]);

  const checkRateLimit = useCallback((): boolean => {
    const now = Date.now();
    
    // Remove actions outside the time window
    actionsRef.current = actionsRef.current.filter(
      (timestamp) => now - timestamp < config.windowMs
    );

    if (actionsRef.current.length >= config.maxActions) {
      const oldestAction = actionsRef.current[0];
      const resetTime = oldestAction + config.windowMs;
      
      setState({
        isLimited: true,
        remainingActions: 0,
        resetTime,
      });
      
      // Auto-reset after window passes
      setTimeout(() => {
        actionsRef.current = actionsRef.current.filter(
          (timestamp) => Date.now() - timestamp < config.windowMs
        );
        setState({
          isLimited: actionsRef.current.length >= config.maxActions,
          remainingActions: Math.max(0, config.maxActions - actionsRef.current.length),
          resetTime: null,
        });
      }, resetTime - now);
      
      return false;
    }

    return true;
  }, [config.maxActions, config.windowMs]);

  const recordAction = useCallback(() => {
    const now = Date.now();
    actionsRef.current.push(now);
    
    // Clean up old actions
    actionsRef.current = actionsRef.current.filter(
      (timestamp) => now - timestamp < config.windowMs
    );
    
    setState({
      isLimited: actionsRef.current.length >= config.maxActions,
      remainingActions: Math.max(0, config.maxActions - actionsRef.current.length),
      resetTime: null,
    });
  }, [config.maxActions, config.windowMs]);

  const executeWithRateLimit = useCallback(
    async <T,>(action: () => Promise<T>): Promise<T | null> => {
      if (!checkRateLimit()) {
        return null;
      }
      
      recordAction();
      return action();
    },
    [checkRateLimit, recordAction]
  );

  return {
    ...state,
    checkRateLimit,
    recordAction,
    executeWithRateLimit,
  };
};

// Preset configurations for common use cases
export const RATE_LIMIT_CONFIGS = {
  bidPlacement: { maxActions: 10, windowMs: 60000 }, // 10 bids per minute
  eventCreation: { maxActions: 5, windowMs: 3600000 }, // 5 events per hour
  profileUpdate: { maxActions: 3, windowMs: 60000 }, // 3 updates per minute
} as const;
